import React, { useState, useEffect } from 'react';
import { localApi } from '../utils/localApi';
import { FileText, Upload, Trash2, AlertTriangle, Loader2 } from 'lucide-react';

export default function BulletinManager() {
    const [bulletins, setBulletins] = useState<any[]>([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchBulletins();
    }, []);

    const fetchBulletins = async () => {
        setLoading(true);
        try {
            const data = await localApi.getBulletins();
            setBulletins(data || []);
        } catch (err: any) {
            setError(err.message);
        }
        setLoading(false);
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        try {
            setUploading(true);
            setError(null);
            const file = e.target.files?.[0];
            if (!file) return;

            if (file.type !== 'application/pdf') {
                throw new Error('Seuls les fichiers PDF sont autorisés.');
            }

            const res = await localApi.uploadBulletin(file);
            if (res.error) throw new Error(res.error);

            fetchBulletins();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Voulez-vous vraiment supprimer ce bulletin ?')) return;

        try {
            const res = await localApi.deleteBulletin(id);
            if (res.error) throw new Error(res.error);
            fetchBulletins();
        } catch (err: any) {
            setError(err.message);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-slate-800/40 border border-white/5 rounded-3xl p-6">
                <h3 className="text-sm font-black text-white uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Upload size={16} className="text-cyan-500" />
                    Nouveau Bulletin PDF
                </h3>

                <label
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                            const event = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
                            handleUpload(event);
                        }
                    }}
                    className="relative group cursor-pointer block border-2 border-dashed border-white/10 hover:border-cyan-500/50 rounded-2xl p-8 transition-all bg-slate-900/40"
                >
                    <input
                        type="file"
                        accept=".pdf"
                        onChange={handleUpload}
                        disabled={uploading}
                        className="hidden"
                    />
                    <div className="text-center space-y-2">
                        {uploading ? (
                            <Loader2 className="w-10 h-10 text-cyan-500 animate-spin mx-auto" />
                        ) : (
                            <FileText className="w-10 h-10 text-slate-600 group-hover:text-cyan-500 transition-all mx-auto" />
                        )}
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {uploading ? 'Envoi en cours...' : 'Cliquez pour sélectionner un PDF'}
                        </p>
                    </div>
                </label>

                {error && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-500 text-[10px] font-bold uppercase tracking-widest">
                        <AlertTriangle size={14} />
                        {error}
                    </div>
                )}
            </div>

            <div className="space-y-3">
                <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest px-2">Bulletins en vigueur</h3>

                {loading && bulletins.length === 0 ? (
                    <div className="p-12 text-center text-slate-600">Chargement...</div>
                ) : bulletins.length === 0 ? (
                    <p className="text-[10px] text-slate-600 font-bold uppercase tracking-widest text-center py-8">Aucun document enregistré</p>
                ) : (
                    bulletins.map(bulletin => (
                        <div key={bulletin.id} className="bg-slate-800/30 border border-white/5 rounded-2xl p-4 flex items-center justify-between group">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-red-500/10 rounded-lg">
                                    <FileText size={18} className="text-red-500" />
                                </div>
                                <div>
                                    <p className="text-xs font-black text-white uppercase tracking-tight">{bulletin.name}</p>
                                    <p className="text-[8px] text-slate-500 font-bold uppercase tracking-widest mt-0.5">
                                        Posté le {new Date(bulletin.created_at).toLocaleDateString('fr-FR')}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => handleDelete(bulletin.id)}
                                className="p-2.5 bg-slate-900/50 hover:bg-red-500/20 text-slate-500 hover:text-red-500 rounded-xl transition-all opacity-0 group-hover:opacity-100 border border-white/5"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
